import { EConnectionEvents, EWebsocketClientEvents, TAllEvents, TWebSocketMessage } from 'webrtc/events';
import { ISignalClient, TListener, TRawConnection, TSignalCallback } from './types';
import logger from 'utils/log';

// 2.5 minutes — keeps ZScaler (if applicable, 5-min idle WebSocket timeout) and API Gateway (10-min) alive
export const HEARTBEAT_INTERVAL_MS = 150_000;
export const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY_MS = 1_000;
// AVS-4302 takeover reliability: bound for signals buffered while the socket is CONNECTING —
// a handshake that never completes must not grow the queue unboundedly.
export const MAX_QUEUED_SIGNALS = 50;

export class SignalerClient implements ISignalClient {
  public wsApiUrl: string;

  private wsClient!: WebSocket;

  private listeners: TListener[];

  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  private isIntentionalDisconnect: boolean = false;

  private reconnectAttempts: number = 0;

  // Latches once the reconnect loop gives up, so WsClientGaveUp fires exactly once per outage.
  // Reset on any successful (re)connect so a later outage can signal again.
  private hasGivenUp: boolean = false;

  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private outboundQueue: TWebSocketMessage[] = [];

  constructor(wsApiUrl: string) {
    this.wsApiUrl = wsApiUrl;
    this.listeners = [];
  }

  public signal(message: TWebSocketMessage) {
    if (!this.wsClient) {
      return;
    }

    if (this.wsClient.readyState === WebSocket.OPEN) {
      this.wsClient.send(JSON.stringify(message));
    } else if (this.wsClient.readyState === WebSocket.CONNECTING) {
      // AVS-4302 takeover reliability: NEVER assign wsClient.onopen here — overwriting it
      // detaches the raw open listener from setRawListeners, which both drops every previously
      // "queued" message but the last AND kills the handshake (HandshakeStart is sent from that
      // listener). Buffer instead; the queue flushes once the handshake completes — sending
      // earlier would race server-side connection registration.
      if (this.outboundQueue.length >= MAX_QUEUED_SIGNALS) {
        logger.warn('[SignalerClient] Outbound queue full — dropping signal', message.action);
        return;
      }
      this.outboundQueue.push(message);
    } else {
      logger.warn('[SignalerClient] Socket closed — dropping signal', message.action);
    }
  }

  private flushOutboundQueue(): void {
    if (!this.outboundQueue.length) return;
    const queued = this.outboundQueue;
    this.outboundQueue = [];
    queued.forEach((message) => this.signal(message));
  }

  private dropOutboundQueue(reason: string): void {
    if (!this.outboundQueue.length) return;
    logger.warn(`[SignalerClient] Dropping ${this.outboundQueue.length} queued signal(s): ${reason}`);
    this.outboundQueue = [];
  }

  public async connect(): Promise<TRawConnection> {
    this.disconnect();
    this.isIntentionalDisconnect = false;
    this.reconnectAttempts = 0;
    this.hasGivenUp = false;
    return new Promise((res, rej) => {
      this.wsClient = new WebSocket(this.wsApiUrl);
      this.setRawListeners();

      // Capture connect()-scoped callbacks so cleanup() only removes these
      // specific registrations and never touches application-level listeners.
      const onOpen = () => {
        this.signal({ action: EConnectionEvents.HandshakeStart, data: {} });
      };

      const onHandshakeComplete = (connection: TRawConnection) => {
        cleanup();
        this.startHeartbeat();
        this.flushOutboundQueue();
        res(connection);
      };

      const onError = (e: unknown) => {
        cleanup();
        rej(e);
      };

      const cleanup = () => {
        this.off(EWebsocketClientEvents.WsClientOpen, onOpen);
        this.off(EConnectionEvents.HandshakeComplete, onHandshakeComplete);
        this.off(EWebsocketClientEvents.WsClientError, onError);
      };

      this.on(EWebsocketClientEvents.WsClientOpen, onOpen);
      this.on(EConnectionEvents.HandshakeComplete, onHandshakeComplete);
      this.on(EWebsocketClientEvents.WsClientError, onError);
    });
  }

  public on(event: TAllEvents, callback: TSignalCallback) {
    this.listeners.push({ event, callback });
  }

  public off(event: TAllEvents, callback?: TSignalCallback) {
    if (callback) {
      this.listeners = this.listeners.filter(({ event: e, callback: cb }) => !(e === event && cb === callback));
    } else {
      this.listeners = this.listeners.filter(({ event: e }) => e !== event);
    }
  }

  public disconnect() {
    this.isIntentionalDisconnect = true;
    this.stopHeartbeat();
    this.clearReconnectTimer();
    this.dropOutboundQueue('intentional disconnect');
    if (!this.wsClient) {
      return;
    }
    if (this.isReady()) {
      this.wsClient.close();
      this.listeners = [];
    }
  }

  private setRawListeners = () => {
    this.wsClient.onopen = () => {
      this.callListener(EWebsocketClientEvents.WsClientOpen);
    };

    this.wsClient.onclose = () => {
      this.callListener(EWebsocketClientEvents.WsClientClosed);
      if (!this.isIntentionalDisconnect) {
        this.stopHeartbeat();
        this.scheduleReconnect();
      }
    };

    this.wsClient.onerror = () => this.callListener(EWebsocketClientEvents.WsClientError);

    this.wsClient.onmessage = (event) => {
      const { action, from, data } = JSON.parse(event.data);
      this.callListener(action, data, from);
    };
  };

  private callListener(event: TAllEvents, data?: Record<string, unknown>, from?: string) {
    this.listeners.forEach((listener) => {
      if (listener.event === event) listener.callback(data, from);
    });
  }

  public isReady() {
    return this.wsClient.readyState === WebSocket.CONNECTING || this.wsClient.readyState === WebSocket.OPEN;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.signal({ action: EConnectionEvents.Ping, data: {} });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.error('[SignalerClient] Max reconnect attempts reached, giving up');
      // Signal degraded mode once so the session layer can fall back to slow-poll read-repair
      // (AVS-4330). Latched: further onclose events in this dead state must not re-fire it.
      if (!this.hasGivenUp) {
        this.hasGivenUp = true;
        this.callListener(EWebsocketClientEvents.WsClientGaveUp);
      }
      return;
    }

    const delay = Math.min(BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts, 30_000);
    this.reconnectAttempts++;
    logger.warn(
      `[SignalerClient] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`
    );
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      this.doReconnect();
    }, delay);
  }

  /**
   * Re-establishes the WebSocket after an unexpected disconnect.
   *
   * Uses a dedicated onmessage handler during the handshake phase so that
   * one-shot setup listeners from the initial connect() call are not reused.
   * After HandshakeComplete the normal setRawListeners() path takes over and
   * WsClientReconnected is dispatched so RTCClient can reset peer state.
   */
  private doReconnect(): void {
    if (this.isIntentionalDisconnect) return;

    const ws = new WebSocket(this.wsApiUrl);

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: EConnectionEvents.HandshakeStart, data: {} }));
    };

    ws.onclose = () => {
      // Failed during reconnect handshake — back off and try again
      if (!this.isIntentionalDisconnect) {
        this.scheduleReconnect();
      }
    };

    ws.onerror = () => {
      // onerror always precedes onclose; let onclose drive the retry
    };

    ws.onmessage = (event) => {
      const { action, data } = JSON.parse(event.data) as { action: string; data: Record<string, unknown> };
      if (action === EConnectionEvents.HandshakeComplete) {
        this.wsClient = ws;
        if (this.isIntentionalDisconnect) {
          ws.close();
          return;
        }
        logger.log('[SignalerClient] Reconnecting with new connectionId:', data.connectionId);
        // Wire up normal message handling (and the onclose reconnect guard)
        this.setRawListeners();
        this.reconnectAttempts = 0;
        this.hasGivenUp = false;
        this.startHeartbeat();
        // A reconnect rebuilds all peer state under a NEW connectionId — anything queued before
        // it is stale, and replaying old PeerSignals would manufacture glare. Drop, never flush.
        this.dropOutboundQueue('reconnect reset — peer state was rebuilt');
        // Notify application layer so it can refresh peer state with the new connectionId
        this.callListener(EWebsocketClientEvents.WsClientReconnected, data);
      }
    };
  }
}
